import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { ITransactionModalData } from '../../time-sheets/time-sheets.type';
import { INote, IPayCategory } from '../../pay-categories/pay-categories.type';
import { TimeSheetsStoreService } from '../../time-sheets/time-sheets.store';

/**
 * dialog for pilotpay
 */
@Component({
	selector: 'app-pilotpay-transactiondialog',
	styleUrls: ['./transactiondialog.component.scss'],
	templateUrl: './transactiondialog.component.html'
})
export class TransactionDialogComponent implements OnInit {
	constructor(
		private fb: FormBuilder,
		private timeSheetsStoreService: TimeSheetsStoreService,
		private dialogRef: MatDialogRef<TransactionDialogComponent>,
		@Inject(MAT_DIALOG_DATA)
		public modalData: ITransactionModalData
	) { }

	public errorMessage: any;
	public form: FormGroup;
	public activePayCategories: IPayCategory[];
	public selectedPayCategory: IPayCategory;
	public isSubmitted: boolean;
	public minDateValue: moment.Moment;
	public maxDateValue: moment.Moment;

	public ngOnInit(): void {
		this.form = this.fb.group({
			transactionDate: [
				this.modalData.transaction ? moment.utc(this.modalData.transaction.transactionDate) : null,
				Validators.required
			],
			value: [this.modalData.transaction ? this.getValue() : null],
			valueMinutes: [this.modalData.transaction ? this.getValueMinutes() : null],
			payCategoryId: [
				this.modalData.transaction ? this.modalData.transaction.payCategoryId : null,
				Validators.required
			],
			comments: [this.modalData?.transaction?.comments || '', Validators.required],
			timeSheetTransactionId: [
				this.modalData.transaction ? this.modalData.transaction.timeSheetTransactionId : null
			],
			timeSheetId: [this.modalData.timesheet ? this.modalData.timesheet.timeSheetId : null],
			timeSheetTransactionNoteValue: this.fb.array([])
		});

		this.activePayCategories = this.modalData.payCategories;

		if (this.form.value.payCategoryId) {
			this.setPayCategoryNotesFields();
		}

		this.minDateValue = moment.utc(this.modalData.timesheet.startDate);
		this.maxDateValue = moment.utc(this.modalData.timesheet.endDate);
		if (
			(this.modalData.timeSheetStatusError && !this.modalData.timeSheetLockOutError) ||
			this.modalData.timeSheetLockOutError
		) {
			this.form.disable();
		}
	}

	public save(): void {
		if (!this.form.invalid) {
			this.isSubmitted = true;
			const formValue: any = this.form.value;
			if (this.form.get('valueMinutes').value != null) {
				formValue.value = parseFloat((this.form.get('value').value + this.form.get('valueMinutes').value / 60).toFixed(2));
			}

			formValue.transactionDate = this.form.value.transactionDate.format('YYYY-MM-DDTHH:mm:ss');
			if (formValue.timeSheetTransactionId) {
				this.updateTransaction(formValue);
			} else {
				this.addTransaction(formValue);
			}
		}
	}

	public close(): void {
		this.dialogRef.close();
	}

	public onPayCategoryChange(): void {
		if (this.modalData.transaction) {
			this.modalData.transaction.timeSheetTransactionNoteValues = [];
		}
		this.form.get('value').patchValue(null);
		this.form.get('valueMinutes').patchValue(null);
		this.form.setControl('timeSheetTransactionNoteValue', this.fb.array([]));
		this.setPayCategoryNotesFields();
		this.setFormValidators();
	}

	public createItem(note: any, index: number): FormGroup {
		const isRequired: boolean = note.isRequired;
		const currentNoteValue: any =
			this.modalData.transaction && this.modalData.transaction.timeSheetTransactionNoteValues[index]
				? this.modalData.transaction.timeSheetTransactionNoteValues[index]
				: null;

		if (note.fieldType === 'PickList' && note.pickListItems && typeof note.pickListItems === 'string') {
			// This check is just for backwards compatibility for notes that were already stored as string
			note.pickListItems = JSON.parse(note.pickListItems);
		} else if (note.fieldType === 'AutoComplete') {
			this.setAutoCompleteList(note);
		}

		return this.fb.group({
			name: note.name,
			isRequired: note.isRequired,
			value: new FormControl(
				currentNoteValue ? currentNoteValue.value : '',
				isRequired ? Validators.required : []
			),
			payCategoryNoteId: note.payCategoryNoteSettingId,
			timeSheetTransactionNoteValueId: currentNoteValue ? currentNoteValue.timeSheetTransactionNoteValueId : ''
		});
	}

	public setAutoCompleteList(note: INote): void {
		this.timeSheetsStoreService.getPreviousNotes(note.payCategoryNoteSettingId).subscribe((response: string[]) => {
			note.autoCompleteItems = response;
		});
	}

	public addTransaction(formValue: any): void {
		this.timeSheetsStoreService.addTransaction(formValue).subscribe(
			(response: any) => {
				this.dialogRef.close(response);
				this.isSubmitted = false;
			},
			(error: any) => {
				this.errorMessage = error;
				this.isSubmitted = false;
			}
		);
	}

	public updateTransaction(formValue: any): void {
		this.timeSheetsStoreService.updateTransaction(formValue).subscribe(
			(response: any) => {
				this.dialogRef.close(response);
				this.isSubmitted = false;
			},
			(error: any) => {
				this.errorMessage = error;
				this.isSubmitted = false;
			}
		);
	}

	public deleteTransaction(): void {
		this.timeSheetsStoreService.deleteTransaction(this.modalData.transaction.timeSheetTransactionId).subscribe(
			(response: any) => {
				this.dialogRef.close({ isDelete: true });
				this.isSubmitted = false;
			},
			(error: any) => {
				this.errorMessage = error;
				this.isSubmitted = false;
			}
		);
	}

	private setPayCategoryNotesFields(): void {
		this.selectedPayCategory = this.activePayCategories.find(
			item => item.payCategoryId.toString() === this.form.value.payCategoryId.toString()
		);

		if (this.selectedPayCategory) {
			this.selectedPayCategory.notes.forEach((note, index) => {
				const control: FormArray = <FormArray>this.form.controls['timeSheetTransactionNoteValue'];
				control.push(this.createItem(note, index));
			});

		}
	}

	private setFormValidators(): void {
		const control: any = this.form.get('valueMinutes');
		if (this.selectedPayCategory.unit === "Hours") {
			control?.setValidators([Validators.required]);
			control?.updateValueAndValidity();
		}
		else {
			control?.setValidators([]);
			control?.updateValueAndValidity()
		}
	}

	private getValue(): number {
		if (this.modalData.transaction.payCategory.unit === "Hours") {
			return Math.floor(this.modalData.transaction.value);
		}
		else {
			return this.modalData.transaction.value;
		}
	}

	private getValueMinutes(): number {
		if (this.modalData.transaction.payCategory.unit === "Hours") {
			const hours: number = Math.floor(this.modalData.transaction.value);
			return Math.round((this.modalData.transaction.value - hours) * 60);
		}
	}
}
